import React, { useEffect, useState, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import { useSortBy, useTable } from "react-table";
import DashboardCard from "./cards/DashboardCard";
import { apiReports } from "../api/reports";
import Sparkline from "./Sparkline";
import SchedulerUtilisationDetailModal from "./SchedulerUtilisationDetailModal";
import SchedulerAreaUtilisationDetailModal from "./SchedulerAreaUtilisationDetailModal";

const green = "#94ba33";
const amber = "rgba(251,191,36)";
const red = "#d71433";
const blue = "#00acdc";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const currentUtcMonth = () => {
  const n = new Date();
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}`;
};
const utilColor = (u) => u >= 0.7 ? green : u >= 0.4 ? amber : red;
const fmtDay = (iso) => {
  if (!iso) {return iso;}
  const d = new Date(String(iso).split("T")[0].split(" ")[0] + "T00:00:00Z");
  if (isNaN(d)) {return iso;}
  return `${d.getUTCDate()} ${d.toLocaleString("en", { month: "short", timeZone: "UTC" })}`;
};

function SchedulerTable({ data, onRowClick }) {
  const columns = useMemo(
    () => [
      { Header: "Scheduler", accessor: "name" },
      { Header: "Scheduled", accessor: "scheduled", sortType: "basic" },
      { Header: "Phone", accessor: (r) => r.texts + r.calls + r.missed, id: "phone", sortType: "basic", Cell: ({ value, row }) => value ? `${value}` : "—" },
      { Header: "Call hrs", accessor: "callHours", sortType: "basic", Cell: ({ value }) => value ? value : "—" },
      { Header: "Sched. days", accessor: "activeDays", sortType: "basic" },
      { Header: "Dwell hrs", accessor: "dwellHours", sortType: "basic" },
      { Header: "In-app hrs", accessor: "pageHours", sortType: "basic" },
      { Header: "Engaged hrs", accessor: "engagedHours", sortType: "basic" },
      { Header: "Report edits", accessor: "reportEdits", sortType: "basic" },
      { Header: "Sched/day", accessor: "scheduledPerDay", sortType: "basic" },
      { Header: "Sched/engaged hr", accessor: "scheduledPerHour", sortType: "basic" },
      {
        Header: "Util %",
        accessor: "utilisation",
        sortType: "basic",
        Cell: ({ value }) => <span style={{ color: utilColor(value), fontWeight: 600 }}>{pct(value)}</span>,
      },
      { Header: "Top area", accessor: "topArea", Cell: ({ value }) => value || "—" },
      {
        Header: "Trend",
        accessor: "weekly",
        disableSortBy: true,
        Cell: ({ value }) =>
          value && value.length > 1 ? <Sparkline data={{ values: value }} color={blue} /> : "—",
      },
    ],
    []
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data, initialState: { sortBy: [{ id: "scheduled", desc: true }] } },
    useSortBy
  );
  return (
    <div className="tw-overflow-x-auto">
      <table {...getTableProps()} className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead>
          {headerGroups.map((hg, i) =>
            <tr key={i} {...hg.getHeaderGroupProps()} className="tw-bg-fs-dark-blue tw-bg-opacity-25">
              {hg.headers.map((col, j) =>
                <th key={j}
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs tw-cursor-pointer"
                >
                  {col.render("Header")}
                  {col.isSorted ? col.isSortedDesc ? " ▼" : " ▲" : ""}
                </th>
              )}
            </tr>
          )}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr key={i} {...row.getRowProps()} className={`${i % 2 ? "tw-bg-gray-100" : ""} tw-cursor-pointer hover:tw-bg-fs-dark-blue hover:tw-bg-opacity-10`}
                onClick={() => onRowClick && onRowClick(row.original)}
              >
                {row.cells.map((cell, j) =>
                  <td key={j} {...cell.getCellProps()} className="tw-px-4 tw-py-2 tw-text-sm">
                    {cell.render("Cell")}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SchedulerUtilisationTab = () => {
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState(""); // "YYYY-MM" calendar-month mode; "" = rolling window
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [schedulerDetailId, setSchedulerDetailId] = useState(null);
  const [schedulerUtilisation, setSchedulerUtilisation] = useState(null);
  const [areaDetailId, setAreaDetailId] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState(null);

  // last 12 calendar months for the picker (current month = month-to-date).
  // Server months are UTC calendar months, so build the options in UTC —
  // computing value/label arithmetically avoids local-getter off-by-ones.
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
    const monthName = (y, m) => new Date(Date.UTC(y, m, 1)).toLocaleString("en", { month: "short", timeZone: "UTC" });
    for (let i = 0; i < 12; i++) {
      let m = utc.getMonth() - i, y = utc.getFullYear();
      while (m < 0) { m += 12; y -= 1; }
      const value = `${y}-${String(m + 1).padStart(2, "0")}`;
      const label = i === 0 ? `${monthName(y, m)} ${y} (to date)` : `${monthName(y, m)} ${y}`;
      opts.push({ value, label });
    }
    return opts;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    apiReports.getSchedulerUtilisation(days, month)
      .then((res) => { if (!cancelled) { setData(res); setError(null); setFetching(false); } })
      .catch((e) => { if (!cancelled) { setError(e); setData(null); setFetching(false); } });
    return () => { cancelled = true; };
  }, [days, month]);

  useEffect(() => {
    let cancelled = false;
    apiReports.getSchedulerMonthlyTrend()
      .then((res) => { if (!cancelled) { const r = res?.data?.result; if (r) { setMonthlyTrend(r); } } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const result = data?.data?.result;
  const initialLoading = !data && !error;
  if (initialLoading) {
    return <div className="tw-flex tw-justify-center tw-py-12"><CircularProgress /></div>;
  }
  if (error || !result) {
    return <div className="tw-py-12 tw-text-center tw-text-fs-red">Failed to load scheduler utilisation data.</div>;
  }

  const s = result.summary;
  const schedulers = result.schedulers || [];
  const weekdays = result.weekday || [];
  const hours = result.hours || [];
  const areas = result.areas || [];
  const trend = result.trend || [];
  const mt = monthlyTrend || { months: [], schedulers: [] };
  const trendColors = [green, blue, amber, red, "#7c3aed", "#0f766e", "#b45309", "#475569"];
  const mtData = {
    labels: mt.months.map((m) => {
      const [y, mm] = m.split("-");
      return new Date(Number(y), Number(mm) - 1, 1).toLocaleString("en", { month: "short" });
    }),
    datasets: (mt.schedulers || []).map((sc, i) => ({
      label: sc.name.trim(),
      data: sc.pageHours,
      borderColor: trendColors[i % trendColors.length],
      backgroundColor: trendColors[i % trendColors.length],
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      lineTension: 0.25,
      fill: false,
    })),
  };
  const mtOptions = {
    maintainAspectRatio: false,
    legend: { position: "top", labels: { boxWidth: 12, fontSize: 10 } },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: "In-app hours" } }],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 12 } }],
    },
    tooltips: { mode: "index", intersect: false },
  };
  // Trend: scheduled per day
  const trendData = {
    labels: trend.map((t) => fmtDay(t.day)),
    datasets: [
      {
        label: "Scheduled",
        data: trend.map((t) => t.scheduled),
        borderColor: green,
        backgroundColor: "rgba(148,186,51,0.12)",
        borderWidth: 2,
        fill: true,
        lineTension: 0.25,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };
  const trendOptions = {
    maintainAspectRatio: false,
    legend: { position: "top" },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Policies / day" } }],
      xAxes: [{ ticks: { autoSkip: true, maxRotation: 45, minRotation: 0, maxTicksLimit: 10, autoSkipPadding: 4 } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  // Weekday made (raw events - timing)
  const weekdayData = {
    labels: weekdays.map((w) => w.name),
    datasets: [{ label: "Activity", data: weekdays.map((w) => w.made), backgroundColor: blue }],
  };
  const weekdayOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Made events" } }] },
    tooltips: { mode: "index", intersect: false },
  };

  // Hour-of-day made (when they work)
  const phoneHours = result.phoneHours || [];
  const phoneHourData = {
    labels: phoneHours.map((h) => `${h.hour}:00`),
    datasets: [{ label: "Phone", data: phoneHours.map((h) => h.made), backgroundColor: blue, maxBarThickness: 16 }],
  };
  const phoneHourOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Outgoing events" } }],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 24, callback: (v) => `${v}:00` }, scaleLabel: { display: true, labelString: "Hour (US/Eastern)" } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  const hourData = {
    labels: hours.map((h) => `${h.hour}:00`),
    datasets: [{ label: "Activity", data: hours.map((h) => h.made), backgroundColor: amber, maxBarThickness: 16 }],
  };
  const hourOpts = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Made events" } }],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 24, callback: (v) => `${v}:00` }, scaleLabel: { display: true, labelString: "Hour (US/Eastern)" } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  return (
    <div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-flex-wrap">
        <span className="tw-text-sm tw-text-gray-600">Window:</span>
        {[7, 30, 90].map((d) =>
          <button key={d} onClick={() => { setDays(d); setMonth(""); }}
            className={`tw-px-3 tw-py-1 tw-rounded-lg tw-text-sm ${!month && days === d ? "tw-bg-fs-dark-blue tw-text-white tw-font-semibold" : "tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300"}`}>
            Last {d} days
          </button>
        )}
        <span className="tw-text-sm tw-text-gray-600 tw-ml-4">Month:</span>
        <select value={month}
          onChange={(e) => setMonth(e.target.value)}
          className={`tw-px-3 tw-py-1 tw-rounded-lg tw-text-sm tw-border ${month ? "tw-bg-fs-dark-blue tw-text-white tw-font-semibold tw-border-fs-dark-blue" : "tw-bg-gray-200 tw-text-gray-700 tw-border-gray-300"}`}>
          <option value="">(rolling window)</option>
          {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        {s.month && <span className="tw-text-xs tw-text-gray-500">calendar month · {s.month}{s.month === currentUtcMonth() ? " · month to date" : ""}</span>}
        {fetching && <span className="tw-ml-2 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500"><CircularProgress size={14} /> loading…</span>}
      </div>

      <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-6 tw-gap-4 md:tw-gap-8 xl:tw-gap-12 tw-my-6">
        <DashboardCard title="Scheduled" value={s.totalScheduled.toLocaleString()} subtitle="distinct policies" color="tw-bg-fs-green" />
        <DashboardCard title="Capacity" value={s.capacity.toLocaleString()} subtitle="flat-out (all weekdays, best rate)" color="tw-bg-gray-600" />
        <DashboardCard title="Utilisation" value={pct(s.capacityUtilisation)} subtitle="scheduled of capacity" color="tw-bg-fs-dark-blue" />
        <DashboardCard title="Engaged hours" value={s.totalEngagedHours.toLocaleString()} subtitle="in-system work time" color="tw-bg-yellow-400" />
        <DashboardCard title="Active schedulers" value={s.activeSchedulers} color="tw-bg-primary" />
        <DashboardCard title="Phone events" value={(s.totalTexts + s.totalCalls + s.totalMissed).toLocaleString()} subtitle={`${s.totalTexts} texts · ${s.totalCalls} calls · ${s.totalCallHours} hrs talk`} color="tw-bg-fs-dark-blue" />
      </div>

      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Monthly in-app hours <span className="tw-text-xs tw-font-normal tw-text-gray-500">(last 12 calendar months — click legend entries to isolate a scheduler)</span></h3>
        <div style={{ height: 300 }}>
          {(mt.schedulers || []).length > 0
            ? <Line data={mtData} options={mtOptions} />
            : (mt.months || []).length > 0
              ? <div className="tw-text-gray-500 tw-text-sm">No scheduler data available.</div>
              : <div className="tw-text-gray-500 tw-text-sm">Trend not loaded.</div>}
        </div>
      </div>

      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Policies scheduled per day <span className="tw-text-xs tw-font-normal tw-text-gray-500">(distinct, de-duped)</span></h3>
        <div style={{ height: 300 }}>
          {trend.length > 1 ? <Line data={trendData} options={trendOptions} /> : <div className="tw-text-gray-500 tw-text-sm">Not enough data at this window.</div>}
        </div>
      </div>

      <div className="tw-grid md:tw-grid-cols-2 tw-gap-6">
        <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm">
          <h3 className="tw-font-semibold tw-mb-2">Activity by weekday <span className="tw-text-xs tw-font-normal tw-text-gray-500">(made events)</span></h3>
          <div style={{ height: 260 }}><Bar data={weekdayData} options={weekdayOpts} /></div>
        </div>
        <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm">
          <h3 className="tw-font-semibold tw-mb-2">Activity by hour <span className="tw-text-xs tw-font-normal tw-text-gray-500">(made events, US/Eastern)</span></h3>
          <div style={{ height: 260 }}><Bar data={hourData} options={hourOpts} /></div>
        </div>
        <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm">
          <h3 className="tw-font-semibold tw-mb-2">Phone by hour <span className="tw-text-xs tw-font-normal tw-text-gray-500">(outgoing, US/Eastern)</span></h3>
          <div style={{ height: 260 }}><Bar data={phoneHourData} options={phoneHourOpts} /></div>
        </div>
      </div>

      {/* Area table */}
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Policies scheduled by area <span className="tw-text-xs tw-font-normal tw-text-gray-500">(click a row for the county map)</span></h3>
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead>
              <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Area</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Scheduled</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">% of total</th>
                <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Schedulers</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((a, i) =>
                <tr key={a.workAreaId} className={`${i % 2 ? "tw-bg-gray-100" : ""} tw-cursor-pointer hover:tw-bg-fs-dark-blue hover:tw-bg-opacity-10`}
                  onClick={() => setAreaDetailId(a.workAreaId)}
                >
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.name}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.scheduled.toLocaleString()}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{pct(a.pctOfTotal)}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-sm">{a.schedulers}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scheduler table */}
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
        <h3 className="tw-font-semibold tw-mb-2">Scheduler utilisation <span className="tw-text-xs tw-font-normal tw-text-gray-500">(click a row for detail)</span></h3>
        <SchedulerTable data={schedulers} onRowClick={(row) => { setSchedulerUtilisation(row.utilisation); setSchedulerDetailId(row.userId); }} />
      </div>

      {(result.pageBreakdown || []).length > 0 &&
        <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-my-6">
          <h3 className="tw-font-semibold tw-mb-2">Time by page <span className="tw-text-xs tw-font-normal tw-text-gray-500">(active: visible + focused + not idle)</span></h3>
          <div className="tw-overflow-x-auto">
            <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
              <thead>
                <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                  <th className="tw-px-4 tw-py-2 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Page</th>
                  <th className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">Hours</th>
                  <th className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-uppercase tw-tracking-wider tw-text-xs">% of total</th>
                </tr>
              </thead>
              <tbody>
                {result.pageBreakdown.map((p, i) =>
                  <tr key={p.page} className={i % 2 ? "tw-bg-gray-100" : ""}>
                    <td className="tw-px-4 tw-py-2 tw-text-sm tw-font-mono">{p.page}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-sm tw-text-right">{p.hours.toLocaleString()}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-sm tw-text-right">{pct(p.pctOfTotal)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>}

      <SchedulerUtilisationDetailModal
        open={schedulerDetailId != null}
        onClose={() => { setSchedulerDetailId(null); setSchedulerUtilisation(null); }}
        userId={schedulerDetailId}
        days={days}
        month={month}
        utilisation={schedulerUtilisation}
      />
      <SchedulerAreaUtilisationDetailModal
        open={areaDetailId != null}
        onClose={() => setAreaDetailId(null)}
        workAreaId={areaDetailId}
        days={days}
        month={month}
      />
    </div>
  );
};

export default SchedulerUtilisationTab;